import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { forwardRef, } from 'react';
import { cn } from "../lib/cn.js";
/**
 * Table primitives.
 *
 * Tables are the main interface of an ERP (docs/UI_UX.md), so density is the
 * governing decision: --row-height is 2rem, padding is tight, and the header
 * sticks so a long list keeps its column meaning while scrolling.
 *
 * Sorting is here, in `SortableHeaderCell`, because every list screen needs it
 * and each was otherwise going to grow its own. It is deliberately only the
 * header: this file owns the affordance and the ARIA, never the data. The
 * column key and direction go to the server, which is where the whole filtered
 * set lives — sorting a page of twenty rows in the browser reorders the page
 * and leaves the other 8968 where they were, which looks like sorting and is
 * not.
 *
 * Column visibility and virtualisation still arrive with TanStack Table when a
 * feature actually needs them — a table with no data to show does not need a
 * virtualiser.
 */
export const TableContainer = forwardRef(({ className, ...props }, ref) => (_jsx("div", { ref: ref, 
    /*
     * `overflow-x-auto`, não `overflow-auto`.
     *
     * `overflow-auto` faz deste contêiner o scrollport mais próximo do
     * `thead sticky` logo abaixo. Só que ele nunca tem altura limitada — ele
     * cresce com a tabela —, então **nunca rola na vertical**, e o
     * `sticky top-0` do `TableHead` nunca ativa: quem rola a página perde os
     * rótulos das colunas e fica olhando uma grade de números sem cabeçalho.
     *
     * Limitando o overflow ao eixo X, a rolagem vertical volta a ser a do
     * `<main>` do shell, que é quem de fato rola — e o cabeçalho gruda nele.
     * De quebra some um scrollport aninhado que capturava a roda do mouse
     * quando o ponteiro estava sobre a tabela.
     */
    className: cn('relative w-full overflow-x-auto rounded-(--radius-lg)', 'border border-(--border-default) bg-(--surface)', className), ...props })));
TableContainer.displayName = 'TableContainer';
export const Table = forwardRef(({ className, ...props }, ref) => (_jsx("table", { ref: ref, className: cn('w-full border-collapse text-(length:--text-sm) tabular-nums', className), ...props })));
Table.displayName = 'Table';
export const TableHead = forwardRef(({ className, ...props }, ref) => (_jsx("thead", { ref: ref, className: cn('sticky top-0 z-10 bg-(--surface-sunken)', '[&_th]:border-b [&_th]:border-(--border-default)', className), ...props })));
TableHead.displayName = 'TableHead';
export const TableBody = forwardRef(({ className, ...props }, ref) => (_jsx("tbody", { ref: ref, className: cn('[&_tr:not(:last-child)]:border-b [&_tr]:border-(--border-subtle)', className), ...props })));
TableBody.displayName = 'TableBody';
/**
 * O que, dentro de uma linha, tem dono — F248.
 *
 * Um clique que nasce num destes é do controle, nunca da linha: a caixinha da
 * seleção em massa marca, o select da etapa troca, o link navega. Antes cada
 * tela parava o clique à mão, e a que esquecia abria a ficha por cima do que a
 * pessoa estava fazendo — *"fica selecionando e abrindo detalhe em vez de só
 * marcar"*. `data-row-ignore` é a saída para o que não é controle mas também
 * não deve abrir.
 */
const OWNS_ITS_CLICK = [
    'a[href]',
    'button',
    'input',
    'label',
    'select',
    'summary',
    'textarea',
    '[contenteditable="true"]',
    '[role="button"]',
    '[role="checkbox"]',
    '[role="combobox"]',
    '[role="link"]',
    '[role="menuitem"]',
    '[role="option"]',
    '[role="switch"]',
    '[data-row-ignore]',
].join(', ');
/** O clique é da LINHA, e não de algo dentro dela nem de um portal. */
const isRowClick = (event) => {
    const row = event.currentTarget;
    const target = event.target;
    /*
     * Só o clique que aconteceu DENTRO da linha. O React sobe o evento pelos
     * portais: um diálogo aberto de uma célula (a impressora da troca de etapa,
     * na Produção) aparece por cima de tudo, mas cada clique nele chegava aqui e
     * abria a ficha de novo — uma por clique.
     */
    if (!(target instanceof Element) || !row.contains(target))
        return false;
    if (event.defaultPrevented)
        return false;
    const owner = target.closest(OWNS_ITS_CLICK);
    if (owner !== null && owner !== row && row.contains(owner))
        return false;
    /*
     * Arrastar sobre o código para copiá-lo termina num clique na linha. Quem
     * selecionou texto queria o texto, não a ficha.
     */
    const selection = window.getSelection();
    if (selection !== null &&
        !selection.isCollapsed &&
        selection.anchorNode !== null &&
        row.contains(selection.anchorNode)) {
        return false;
    }
    return true;
};
export const TableRow = forwardRef(({ className, selected = false, onOpen, selecting = false, onToggle, onClick, ...props }, ref) => {
    const action = selecting && onToggle !== undefined ? onToggle : onOpen;
    return (_jsx("tr", { ref: ref, "aria-selected": selected || undefined, onClick: (event) => {
            // O `onClick` de quem chama soma ao da linha, em vez de substituí-lo.
            onClick?.(event);
            if (action === undefined || !isRowClick(event))
                return;
            action();
        }, className: cn(
        // `group/row`: a coluna de ações presa acompanha o hover e a seleção.
        'group/row h-(--row-height) transition-colors duration-(--duration-fast)', 'hover:bg-(--surface-hover)', 'aria-selected:bg-(--surface-selected)', action === undefined ? '' : 'cursor-pointer', className), ...props }));
});
TableRow.displayName = 'TableRow';
/**
 * O que o Tab alcança e o Enter aperta, dentro da primeira célula — F151.
 *
 * A linha inteira responde ao clique (`TableRow onOpen`), mas quem navega por
 * teclado precisa de um elemento focável de verdade. Ele fica no nome do
 * registro, que é o que a pessoa leria em voz alta, e **para a propagação**
 * para a ficha não abrir duas vezes num clique só.
 */
export const RowOpener = forwardRef(({ className, onOpen, children, ...props }, ref) => (_jsx("button", { ref: ref, type: "button", onClick: (event) => {
        event.stopPropagation();
        onOpen();
    }, className: cn('flex max-w-full items-center gap-2 text-left', 'rounded-(--radius-sm) hover:underline', 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--border-focus)', className), ...props, children: children })));
RowOpener.displayName = 'RowOpener';
export const TableHeaderCell = forwardRef(({ className, numeric = false, ...props }, ref) => (_jsx("th", { ref: ref, scope: "col", className: cn('px-3 py-1.5 text-(length:--text-xs) font-(--weight-semibold) uppercase tracking-wide', 'text-(--text-secondary) whitespace-nowrap', numeric ? 'text-right' : 'text-left', className), ...props })));
TableHeaderCell.displayName = 'TableHeaderCell';
export const SORT_LABELS = {
    text: { asc: 'de A a Z', desc: 'de Z a A' },
    number: { asc: 'do menor para o maior', desc: 'do maior para o menor' },
    date: {
        asc: 'do mais antigo para o mais recente',
        desc: 'do mais recente para o mais antigo',
    },
};
/**
 * A column header that sorts when clicked.
 *
 * A real `<button>` inside the `<th>` rather than a click handler on the cell,
 * because sorting a table is an action and the whole cell is not: a `<th>` with
 * an `onClick` is invisible to the keyboard and silent to a screen reader,
 * which turns "ordenar por valor" into a mouse-only feature.
 *
 * `aria-sort` goes on the `<th>` itself — that is where the attribute is
 * defined to live, and it is what makes a screen reader announce the column as
 * sorted instead of leaving the arrow as decoration only sighted users can
 * read.
 *
 * The first click on an unsorted column picks the direction people actually
 * want first, which is not the same for every column: names read A→Z, money
 * and dates read biggest and newest first. Guessing wrong costs a second click
 * every single time.
 */
export const SortableHeaderCell = forwardRef(({ className, numeric = false, sorted, onSort, kind, children, ...props }, ref) => {
    const columnKind = kind ?? (numeric ? 'number' : 'text');
    const first = columnKind === 'text' ? 'asc' : 'desc';
    const next = sorted === false ? first : sorted === 'asc' ? 'desc' : 'asc';
    const Icon = sorted === 'asc'
        ? ArrowUp
        : sorted === 'desc'
            ? ArrowDown
            : ChevronsUpDown;
    return (_jsx("th", { ref: ref, scope: "col", "aria-sort": sorted === false
            ? 'none'
            : sorted === 'asc'
                ? 'ascending'
                : 'descending', className: cn('p-0 text-(length:--text-xs) font-(--weight-semibold) uppercase tracking-wide', 'text-(--text-secondary) whitespace-nowrap', className), ...props, children: _jsxs("button", { type: "button", onClick: () => {
                onSort(next);
            }, title: `Ordenar ${SORT_LABELS[columnKind][next]}`, className: cn('flex w-full items-center gap-1 px-3 py-1.5', 'uppercase tracking-wide transition-colors duration-(--duration-fast)', 'hover:text-(--text-primary)', sorted === false ? '' : 'text-(--text-primary)', numeric ? 'justify-end' : 'justify-start'), children: [numeric ? (_jsx(Icon, { "aria-hidden": "true", className: cn('h-3 w-3 shrink-0', sorted === false ? 'opacity-40' : '') })) : null, _jsx("span", { className: "truncate", children: children }), numeric ? null : (_jsx(Icon, { "aria-hidden": "true", className: cn('h-3 w-3 shrink-0', sorted === false ? 'opacity-40' : '') }))] }) }));
});
SortableHeaderCell.displayName = 'SortableHeaderCell';
export const TableCell = forwardRef(({ className, numeric = false, ...props }, ref) => (_jsx("td", { ref: ref, className: cn('px-3 py-1 text-(--text-primary)', 
    // Money and quantities align right and share digit widths so columns
    // scan vertically. Master Spec section 9 forbids float money; the UI
    // renders whatever decimal string the domain provides.
    numeric ? 'text-right font-(--font-mono)' : 'text-left', className), ...props })));
TableCell.displayName = 'TableCell';
/**
 * A coluna de ações, presa à direita — F248.
 *
 * Numa tela estreita (o tablet do balcão, um notebook de 1366), a tabela de dez
 * colunas de Vendas rola na horizontal, e Retirar, Entregar e Cancelar ficavam
 * fora da tela — a ação principal do caixa escondida atrás de uma rolagem que
 * ninguém sabia que existia. Presa, ela fica sempre à vista; e a célula inteira
 * é das ações (`data-row-ignore`): errar o botão por pouco não abre a ficha.
 */
export const TableActionsCell = forwardRef(({ className, ...props }, ref) => (_jsx(TableCell, { ref: ref, "data-row-ignore": "", className: cn('sticky right-0 z-[1] bg-(--surface)', 'group-hover/row:bg-(--surface-hover) group-aria-selected/row:bg-(--surface-selected)', 'shadow-[inset_1px_0_0_var(--border-subtle)]', className), ...props })));
TableActionsCell.displayName = 'TableActionsCell';
/** O cabeçalho da coluna de ações, preso junto com ela. */
export const TableActionsHeaderCell = forwardRef(({ className, ...props }, ref) => (_jsx(TableHeaderCell, { ref: ref, className: cn('sticky right-0 z-[1] bg-(--surface-sunken)', 'shadow-[inset_1px_0_0_var(--border-subtle)]', className), ...props })));
TableActionsHeaderCell.displayName = 'TableActionsHeaderCell';
//# sourceMappingURL=table.js.map